import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { CreateInvoiceDto, GSTType, Invoice, InvoiceType } from '../../services/invoice.service';
import { inventoryService } from '../../services/inventory.service';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { useHSNCodes, useInvoiceSeller } from '../../hooks/useInvoices';
import { Customer, INDIAN_STATES } from '../../types/customer.types';
import {
  Field,
  formatMoney,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';
import { useActiveGstRates } from '../../hooks/useGstRates';

interface InvoiceFormProps {
  // When set, the form edits this draft invoice; otherwise it creates a new one
  invoice?: Invoice | null;
  onSubmit: (data: CreateInvoiceDto) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

interface ItemForm {
  key: string;
  productId: string;
  productName: string;
  hsnCode: string;
  quantity: string;
  unit: string;
  rate: string;
  discount: string;
  gstRate: string;
  // Compensation cess of the HSN code; most goods have none
  cessRate: string;
}

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  tax_invoice: 'Tax Invoice',
  bill_of_supply: 'Bill of Supply',
  export_invoice: 'Export Invoice',
  debit_note: 'Debit Note',
  credit_note: 'Credit Note',
};

export const GST_TYPE_LABELS: Record<GSTType, string> = {
  cgst_sgst: 'CGST + SGST',
  igst: 'IGST',
  exempt: 'Exempt',
  zero_rated: 'Zero rated',
};

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const stateName = (code: string) => INDIAN_STATES.find((state) => state.gstCode === code)?.name || '';

// The GST state of a customer: from the GST number when registered, otherwise from the address
const customerStateCode = (customer?: Customer) => {
  if (!customer) return '';
  const fromGstin = customer.gstNumber ? customer.gstNumber.slice(0, 2) : '';
  if (fromGstin && stateName(fromGstin)) return fromGstin;
  return INDIAN_STATES.find((state) => state.name.toLowerCase() === (customer.state || '').toLowerCase())?.gstCode || '';
};

const toDateInput = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const addDays = (dateInput: string, days: number) => {
  const [year, month, day] = dateInput.split('-').map(Number);
  return toDateInput(new Date(year, month - 1, day + days));
};

const toNumber = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

let itemKey = 0;
const newItem = (gstRate: number): ItemForm => ({
  key: `item-${++itemKey}`,
  productId: '',
  productName: '',
  hsnCode: '',
  quantity: '1',
  unit: '',
  rate: '',
  discount: '',
  gstRate: String(gstRate),
  cessRate: '0',
});

// The amounts of one line, worked out the same way the server does
const calculateLine = (item: ItemForm, gstType: GSTType) => {
  const gross = toNumber(item.quantity) * toNumber(item.rate);
  const discountAmount = round2((gross * toNumber(item.discount)) / 100);
  const taxable = round2(gross - discountAmount);
  const rate = toNumber(item.gstRate);
  const half = gstType === 'cgst_sgst' ? round2((taxable * round2(rate / 2)) / 100) : 0;
  const igst = gstType === 'igst' ? round2((taxable * rate) / 100) : 0;
  const taxed = gstType === 'cgst_sgst' || gstType === 'igst';
  const cess = taxed ? round2((taxable * toNumber(item.cessRate)) / 100) : 0;
  return { discountAmount, taxable, cgst: half, sgst: half, igst, cess, total: round2(taxable + half + half + igst + cess) };
};

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, title, description, action, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
          {step}
        </span>
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const sectionGrid = 'grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2';

const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoice, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!invoice;
  const today = toDateInput(new Date());

  const [form, setForm] = useState({
    customerId: invoice?.customerId || '',
    invoiceType: (invoice?.invoiceType || 'tax_invoice') as InvoiceType,
    invoiceDate: invoice ? String(invoice.invoiceDate).slice(0, 10) : today,
    dueDate: invoice ? String(invoice.dueDate).slice(0, 10) : addDays(today, 30),
    placeOfSupplyCode: invoice?.placeOfSupplyCode || '',
    reverseCharge: invoice?.reverseCharge ? 'yes' : 'no',
    supplierName: invoice?.supplierName || '',
    supplierAddress: invoice?.supplierAddress || '',
    supplierGSTIN: invoice?.supplierGSTIN || '',
    bankName: invoice?.bankName || '',
    bankAccountNumber: invoice?.bankAccountNumber || '',
    bankIFSC: invoice?.bankIFSC || '',
    bankBranch: invoice?.bankBranch || '',
    notes: invoice?.notes || '',
    termsAndConditions: invoice?.termsAndConditions || '',
  });

  // GST rates come from the GST rate master
  const { optionsFor, defaultRate, isLoaded: ratesLoaded } = useActiveGstRates();

  const [items, setItems] = useState<ItemForm[]>(() =>
    invoice?.items?.length
      ? invoice.items.map((item) => ({
          key: `item-${++itemKey}`,
          productId: item.productId,
          productName: item.productName,
          hsnCode: item.hsnCode,
          quantity: String(item.quantity),
          unit: item.unit,
          rate: String(item.rate),
          discount: item.discount ? String(item.discount) : '',
          gstRate: String(round2(item.cgstRate + item.sgstRate + item.igstRate)),
          cessRate: String(item.cessRate || 0),
        }))
      : [newItem(defaultRate)],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Lines nothing was picked on yet take the default rate of the master once it has loaded
  useEffect(() => {
    if (!ratesLoaded) return;
    setItems((prev) => prev.map((item) => (item.productId || item.hsnCode ? item : { ...item, gstRate: String(defaultRate) })));
  }, [ratesLoaded, defaultRate]);

  const { customers } = useActiveCustomers();
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => inventoryService.getProducts(),
  });
  const { data: hsnCodes = [] } = useHSNCodes();
  const { data: seller } = useInvoiceSeller(!isEdit);

  // A new invoice starts with the seller and bank details already on file
  const sellerApplied = useRef(false);
  useEffect(() => {
    if (isEdit || !seller || sellerApplied.current) return;
    sellerApplied.current = true;
    setForm((prev) => ({
      ...prev,
      supplierName: prev.supplierName || seller.name,
      supplierAddress: prev.supplierAddress || seller.address,
      supplierGSTIN: prev.supplierGSTIN || seller.gstin,
      bankName: prev.bankName || seller.bankName,
      bankAccountNumber: prev.bankAccountNumber || seller.bankAccountNumber,
      bankIFSC: prev.bankIFSC || seller.bankIFSC,
      bankBranch: prev.bankBranch || seller.bankBranch,
      // The terms, notes and due date a new invoice starts with come from the company settings
      termsAndConditions: prev.termsAndConditions || seller.termsAndConditions || '',
      notes: prev.notes || seller.notes || '',
      dueDate:
        !prev.customerId && prev.invoiceDate && seller.paymentTermDays !== undefined
          ? addDays(prev.invoiceDate, seller.paymentTermDays)
          : prev.dueDate,
    }));
  }, [seller, isEdit]);

  // Days until an invoice is due when the customer has no payment terms of its own
  const defaultTermDays = seller?.paymentTermDays ?? 30;

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'customerId') {
        // The customer decides where the goods are supplied and when payment is due
        const customer = customers.find((c) => c.id === value);
        if (customer) {
          next.placeOfSupplyCode = customerStateCode(customer) || prev.placeOfSupplyCode;
          if (prev.invoiceDate) next.dueDate = addDays(prev.invoiceDate, customer.paymentTerms || defaultTermDays);
        }
      }
      if (name === 'invoiceDate' && value) {
        const customer = customers.find((c) => c.id === prev.customerId);
        next.dueDate = addDays(value, customer?.paymentTerms || defaultTermDays);
      }
      if (name === 'supplierGSTIN') next.supplierGSTIN = value.toUpperCase().replace(/\s/g, '');
      if (name === 'bankIFSC') next.bankIFSC = value.toUpperCase().replace(/\s/g, '');
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const setItemField = (index: number, name: keyof ItemForm, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, [name]: value };

        if (name === 'productId') {
          const product = products.find((p) => p.id === value);
          if (product) {
            next.productName = product.name;
            next.unit = product.unit || '';
            next.rate = String(Number(product.price) || 0);
          }
        }
        if (name === 'hsnCode') {
          next.hsnCode = value.replace(/\D/g, '').slice(0, 8);
          // A known HSN code brings its GST rate
          const hsn = hsnCodes.find((code) => code.code === next.hsnCode);
          if (hsn) {
            next.gstRate = String(hsn.igstRate);
            next.cessRate = String(hsn.cessRate || 0);
          }
        }
        return next;
      }),
    );
    const errorKey = `item_${index}_${name}`;
    if (errors[errorKey] || errors.items) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', items: '' }));
    }
  };

  const sellerStateCode = GSTIN_PATTERN.test(form.supplierGSTIN) ? form.supplierGSTIN.slice(0, 2) : '';
  const gstType: GSTType =
    form.invoiceType === 'bill_of_supply'
      ? 'exempt'
      : form.invoiceType === 'export_invoice'
        ? 'zero_rated'
        : sellerStateCode && form.placeOfSupplyCode && sellerStateCode !== form.placeOfSupplyCode
          ? 'igst'
          : 'cgst_sgst';
  const isTaxed = gstType === 'cgst_sgst' || gstType === 'igst';

  const totals = useMemo(() => {
    const lines = items.map((item) => calculateLine(item, gstType));
    const sum = (pick: (line: (typeof lines)[number]) => number) => round2(lines.reduce((total, line) => total + pick(line), 0));
    const taxable = sum((line) => line.taxable);
    const cgst = sum((line) => line.cgst);
    const sgst = sum((line) => line.sgst);
    const igst = sum((line) => line.igst);
    const cess = sum((line) => line.cess);
    const exact = round2(taxable + cgst + sgst + igst + cess);
    const grandTotal = Math.round(exact);
    return {
      lines,
      taxable,
      discount: sum((line) => line.discountAmount),
      cgst,
      sgst,
      igst,
      cess,
      roundOff: round2(grandTotal - exact),
      grandTotal,
    };
  }, [items, gstType]);

  const validate = () => {
    const next: Record<string, string> = {};

    if (!form.customerId) next.customerId = 'Customer is required';
    if (!form.invoiceDate) next.invoiceDate = 'Invoice date is required';
    if (!form.dueDate) next.dueDate = 'Due date is required';
    else if (form.invoiceDate && form.dueDate < form.invoiceDate) next.dueDate = 'Due date cannot be before the invoice date';
    if (!form.placeOfSupplyCode) next.placeOfSupplyCode = 'Place of supply is required';

    if (form.supplierName.trim().length < 2) next.supplierName = 'Seller name is required';
    if (form.supplierAddress.trim().length < 5) next.supplierAddress = 'Seller address is required';
    if (!form.supplierGSTIN) next.supplierGSTIN = 'Seller GST number is required';
    else if (!GSTIN_PATTERN.test(form.supplierGSTIN)) next.supplierGSTIN = 'A GST number has 15 characters, like 27ABCDE1234F1Z5';
    else if (!stateName(form.supplierGSTIN.slice(0, 2))) next.supplierGSTIN = 'The first two digits are not a valid GST state code';

    if (items.length === 0) next.items = 'Add at least one item';
    items.forEach((item, index) => {
      if (!item.productId) next[`item_${index}_productId`] = 'Product is required';
      if (!/^[0-9]{4,8}$/.test(item.hsnCode)) next[`item_${index}_hsnCode`] = 'Enter 4 to 8 digits';
      if (item.quantity === '' || toNumber(item.quantity) <= 0) next[`item_${index}_quantity`] = 'Must be above 0';
      if (item.rate === '' || toNumber(item.rate) < 0) next[`item_${index}_rate`] = 'Enter the rate';
      const discount = toNumber(item.discount);
      if (discount < 0 || discount > 100) next[`item_${index}_discount`] = 'Must be 0 to 100';
      const rate = toNumber(item.gstRate);
      if (item.gstRate === '' || rate < 0 || rate > 100) next[`item_${index}_gstRate`] = 'Must be 0 to 100';
    });

    if (form.notes.length > 2000) next.notes = 'Notes can be at most 2000 characters';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // On an edit, null clears a value that was removed in the form
    const optional = (value: string) => value.trim() || (isEdit ? null : undefined);

    const payload: CreateInvoiceDto = {
      invoiceType: form.invoiceType,
      invoiceDate: form.invoiceDate,
      dueDate: form.dueDate,
      customerId: form.customerId,
      placeOfSupplyCode: form.placeOfSupplyCode,
      reverseCharge: form.reverseCharge === 'yes',
      supplierName: form.supplierName.trim(),
      supplierAddress: form.supplierAddress.trim(),
      supplierGSTIN: form.supplierGSTIN,
      bankName: optional(form.bankName),
      bankAccountNumber: optional(form.bankAccountNumber),
      bankIFSC: optional(form.bankIFSC),
      bankBranch: optional(form.bankBranch),
      notes: optional(form.notes),
      termsAndConditions: optional(form.termsAndConditions),
      items: items.map((item) => ({
        productId: item.productId,
        productName: item.productName || undefined,
        hsnCode: item.hsnCode,
        quantity: toNumber(item.quantity),
        unit: item.unit.trim() || undefined,
        rate: toNumber(item.rate),
        discount: toNumber(item.discount),
        gstRate: toNumber(item.gstRate),
        cessRate: toNumber(item.cessRate),
      })),
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const selectedCustomer = customers.find((customer) => customer.id === form.customerId);
  const customerMissing = isEdit && !!invoice && !customers.some((customer) => customer.id === invoice.customerId);

  const taxReason = !isTaxed
    ? form.invoiceType === 'bill_of_supply'
      ? 'A bill of supply carries no GST'
      : 'An export is zero rated'
    : !sellerStateCode || !form.placeOfSupplyCode
      ? 'Decided by the seller state and the place of supply'
      : gstType === 'igst'
        ? `${stateName(sellerStateCode)} to ${stateName(form.placeOfSupplyCode)}: another state`
        : `Seller and place of supply are both in ${stateName(sellerStateCode)}`;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="Invoice details" description="Who is billed, when, and where the goods are supplied.">
            <div className={sectionGrid}>
              <Field label="Customer" htmlFor="inv-customer" required error={errors.customerId} className="sm:col-span-2">
                <select
                  id="inv-customer"
                  value={form.customerId}
                  onChange={(e) => setField('customerId', e.target.value)}
                  className={fieldClass('customerId')}
                >
                  <option value="">Select a customer</option>
                  {/* Keeps the customer of a draft selectable even if it was deactivated since */}
                  {customerMissing && invoice && <option value={invoice.customerId}>{invoice.customerName}</option>}
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.code})
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Invoice Type" htmlFor="inv-type">
                <select
                  id="inv-type"
                  value={form.invoiceType}
                  onChange={(e) => setField('invoiceType', e.target.value)}
                  className={inputClass}
                >
                  {(Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[]).map((type) => (
                    <option key={type} value={type}>
                      {INVOICE_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Place of Supply"
                htmlFor="inv-place"
                required
                error={errors.placeOfSupplyCode}
                hint="The state the goods are delivered to. It decides CGST + SGST or IGST."
              >
                <select
                  id="inv-place"
                  value={form.placeOfSupplyCode}
                  onChange={(e) => setField('placeOfSupplyCode', e.target.value)}
                  className={fieldClass('placeOfSupplyCode')}
                >
                  <option value="">Select a state</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state.gstCode} value={state.gstCode}>
                      {state.name} ({state.gstCode})
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Invoice Date" htmlFor="inv-date" required error={errors.invoiceDate}>
                <input
                  id="inv-date"
                  type="date"
                  value={form.invoiceDate}
                  onChange={(e) => setField('invoiceDate', e.target.value)}
                  className={fieldClass('invoiceDate')}
                />
              </Field>

              <Field
                label="Due Date"
                htmlFor="inv-due"
                required
                error={errors.dueDate}
                hint={
                  selectedCustomer
                    ? `${selectedCustomer.name} pays within ${selectedCustomer.paymentTerms} days.`
                    : 'Set from the payment terms of the customer.'
                }
              >
                <input
                  id="inv-due"
                  type="date"
                  min={form.invoiceDate}
                  value={form.dueDate}
                  onChange={(e) => setField('dueDate', e.target.value)}
                  className={fieldClass('dueDate')}
                />
              </Field>

              <Field
                label="Reverse Charge"
                htmlFor="inv-reverse"
                hint="Yes when the customer pays the GST to the government instead of you."
              >
                <select
                  id="inv-reverse"
                  value={form.reverseCharge}
                  onChange={(e) => setField('reverseCharge', e.target.value)}
                  className={inputClass}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </Field>
            </div>
          </Section>

          <Section
            step={2}
            title="Seller"
            description={
              isEdit
                ? 'Your company as it is printed on this invoice.'
                : seller?.source === 'settings'
                  ? 'Taken from Company Settings.'
                  : seller?.source === 'company'
                    ? 'Taken from the primary company under Master Data.'
                  : seller?.source === 'last_invoice'
                    ? 'Copied from your latest invoice.'
                    : 'Your company as it is printed on the invoice. Later invoices start with these details.'
            }
          >
            <div className={sectionGrid}>
              <Field label="Seller Name" htmlFor="inv-seller-name" required error={errors.supplierName}>
                <input
                  id="inv-seller-name"
                  type="text"
                  value={form.supplierName}
                  onChange={(e) => setField('supplierName', e.target.value)}
                  className={fieldClass('supplierName')}
                  placeholder="Your company name"
                />
              </Field>

              <Field
                label="Seller GST Number"
                htmlFor="inv-seller-gstin"
                required
                error={errors.supplierGSTIN}
                hint={sellerStateCode ? `Registered in ${stateName(sellerStateCode)} (${sellerStateCode}).` : undefined}
              >
                <input
                  id="inv-seller-gstin"
                  type="text"
                  maxLength={15}
                  value={form.supplierGSTIN}
                  onChange={(e) => setField('supplierGSTIN', e.target.value)}
                  className={cn(fieldClass('supplierGSTIN'), 'font-mono')}
                  placeholder="27ABCDE1234F1Z5"
                />
              </Field>

              <Field
                label="Seller Address"
                htmlFor="inv-seller-address"
                required
                error={errors.supplierAddress}
                className="sm:col-span-2"
              >
                <input
                  id="inv-seller-address"
                  type="text"
                  value={form.supplierAddress}
                  onChange={(e) => setField('supplierAddress', e.target.value)}
                  className={fieldClass('supplierAddress')}
                  placeholder="Building, street, city, pincode"
                />
              </Field>
            </div>
          </Section>

          <Section
            step={3}
            title="Items"
            description="What is being billed. Discount and GST are percentages."
            action={
              <button
                type="button"
                onClick={() => setItems((prev) => [...prev, newItem(defaultRate)])}
                className="inline-flex shrink-0 items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Item
              </button>
            }
          >
            <div className="space-y-3 p-4">
              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}

              {items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center">
                  <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm font-medium text-gray-900">No items yet</p>
                  <p className="mt-1 text-sm text-gray-500">Add the products this invoice bills.</p>
                </div>
              ) : (
                items.map((item, index) => {
                  const line = totals.lines[index];
                  // The rates of the GST rate master, plus the rate this line already has
                  const slabs = optionsFor(toNumber(item.gstRate)).map(String);
                  return (
                    <div key={item.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Item {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                          disabled={items.length === 1}
                          className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-40"
                          title="Remove item"
                          aria-label="Remove item"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-12">
                        <Field
                          label="Product"
                          htmlFor={`inv-item-${index}-product`}
                          required
                          className="col-span-2 sm:col-span-6"
                          error={errors[`item_${index}_productId`]}
                        >
                          <select
                            id={`inv-item-${index}-product`}
                            value={item.productId}
                            onChange={(e) => setItemField(index, 'productId', e.target.value)}
                            className={fieldClass(`item_${index}_productId`)}
                          >
                            <option value="">{productsLoading ? 'Loading products...' : 'Select a product'}</option>
                            {/* A product that is no longer in the list stays on the line */}
                            {item.productId && !productsLoading && !products.some((p) => p.id === item.productId) && (
                              <option value={item.productId}>{item.productName}</option>
                            )}
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.sku}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field
                          label="HSN Code"
                          htmlFor={`inv-item-${index}-hsn`}
                          required
                          className="sm:col-span-3"
                          error={errors[`item_${index}_hsnCode`]}
                        >
                          <input
                            id={`inv-item-${index}-hsn`}
                            type="text"
                            inputMode="numeric"
                            list="inv-hsn-codes"
                            value={item.hsnCode}
                            onChange={(e) => setItemField(index, 'hsnCode', e.target.value)}
                            className={cn(fieldClass(`item_${index}_hsnCode`), 'font-mono')}
                            placeholder="e.g. 8708"
                          />
                        </Field>

                        <Field
                          label="GST (%)"
                          htmlFor={`inv-item-${index}-gst`}
                          className="sm:col-span-3"
                          error={errors[`item_${index}_gstRate`]}
                        >
                          <select
                            id={`inv-item-${index}-gst`}
                            value={String(toNumber(item.gstRate))}
                            onChange={(e) => setItemField(index, 'gstRate', e.target.value)}
                            disabled={!isTaxed}
                            className={fieldClass(`item_${index}_gstRate`)}
                          >
                            {slabs.map((slab) => (
                              <option key={slab} value={slab}>
                                {slab}%
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field
                          label="Quantity"
                          htmlFor={`inv-item-${index}-qty`}
                          required
                          className="sm:col-span-3"
                          error={errors[`item_${index}_quantity`]}
                        >
                          <input
                            id={`inv-item-${index}-qty`}
                            type="number"
                            min="0"
                            step="any"
                            value={item.quantity}
                            onChange={(e) => setItemField(index, 'quantity', e.target.value)}
                            className={fieldClass(`item_${index}_quantity`)}
                          />
                        </Field>

                        <Field label="Unit" htmlFor={`inv-item-${index}-unit`} className="sm:col-span-3">
                          <input
                            id={`inv-item-${index}-unit`}
                            type="text"
                            maxLength={20}
                            value={item.unit}
                            onChange={(e) => setItemField(index, 'unit', e.target.value)}
                            className={inputClass}
                            placeholder="nos"
                          />
                        </Field>

                        <Field
                          label="Rate (₹)"
                          htmlFor={`inv-item-${index}-rate`}
                          required
                          className="sm:col-span-3"
                          error={errors[`item_${index}_rate`]}
                        >
                          <input
                            id={`inv-item-${index}-rate`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => setItemField(index, 'rate', e.target.value)}
                            className={fieldClass(`item_${index}_rate`)}
                            placeholder="0.00"
                          />
                        </Field>

                        <Field
                          label="Discount (%)"
                          htmlFor={`inv-item-${index}-discount`}
                          className="sm:col-span-3"
                          error={errors[`item_${index}_discount`]}
                        >
                          <input
                            id={`inv-item-${index}-discount`}
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.discount}
                            onChange={(e) => setItemField(index, 'discount', e.target.value)}
                            className={fieldClass(`item_${index}_discount`)}
                            placeholder="0"
                          />
                        </Field>
                      </div>

                      <p className="mt-3 flex flex-wrap justify-end gap-x-4 gap-y-1 border-t border-gray-200 pt-2 text-sm text-gray-600">
                        <span>
                          Taxable <span className="font-medium text-gray-900 tabular-nums">{formatMoney(line.taxable)}</span>
                        </span>
                        <span>
                          GST{' '}
                          <span className="font-medium text-gray-900 tabular-nums">
                            {formatMoney(line.cgst + line.sgst + line.igst)}
                          </span>
                        </span>
                        {line.cess > 0 && (
                          <span>
                            Cess ({toNumber(item.cessRate)}%){' '}
                            <span className="font-medium text-gray-900 tabular-nums">{formatMoney(line.cess)}</span>
                          </span>
                        )}
                        <span>
                          Line total{' '}
                          <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(line.total)}</span>
                        </span>
                      </p>
                    </div>
                  );
                })
              )}

              <datalist id="inv-hsn-codes">
                {hsnCodes.map((hsn) => (
                  <option key={hsn.id} value={hsn.code}>
                    {`${hsn.igstRate}% - ${hsn.description.slice(0, 60)}`}
                  </option>
                ))}
              </datalist>
            </div>
          </Section>

          <Section step={4} title="Payment details and notes" description="Printed at the bottom of the invoice.">
            <div className={sectionGrid}>
              <Field label="Bank Name" htmlFor="inv-bank-name">
                <input
                  id="inv-bank-name"
                  type="text"
                  value={form.bankName}
                  onChange={(e) => setField('bankName', e.target.value)}
                  className={inputClass}
                />
              </Field>

              <Field label="Account Number" htmlFor="inv-bank-account">
                <input
                  id="inv-bank-account"
                  type="text"
                  value={form.bankAccountNumber}
                  onChange={(e) => setField('bankAccountNumber', e.target.value)}
                  className={cn(inputClass, 'font-mono')}
                />
              </Field>

              <Field label="IFSC Code" htmlFor="inv-bank-ifsc">
                <input
                  id="inv-bank-ifsc"
                  type="text"
                  maxLength={11}
                  value={form.bankIFSC}
                  onChange={(e) => setField('bankIFSC', e.target.value)}
                  className={cn(inputClass, 'font-mono')}
                />
              </Field>

              <Field label="Branch" htmlFor="inv-bank-branch">
                <input
                  id="inv-bank-branch"
                  type="text"
                  value={form.bankBranch}
                  onChange={(e) => setField('bankBranch', e.target.value)}
                  className={inputClass}
                />
              </Field>

              <Field label="Notes" htmlFor="inv-notes" error={errors.notes}>
                <textarea
                  id="inv-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  className={fieldClass('notes')}
                  placeholder="Anything the customer should know"
                />
              </Field>

              <Field label="Terms and Conditions" htmlFor="inv-terms">
                <textarea
                  id="inv-terms"
                  rows={3}
                  value={form.termsAndConditions}
                  onChange={(e) => setField('termsAndConditions', e.target.value)}
                  className={inputClass}
                  placeholder="Payment and delivery terms"
                />
              </Field>
            </div>
          </Section>
        </div>

        {/* Right: the tax type and the totals so far */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Bill to</p>
                  <p className="font-medium text-gray-900">
                    {selectedCustomer?.name || (customerMissing ? invoice?.customerName : 'Not selected yet')}
                  </p>
                  {selectedCustomer && (
                    <p className="font-mono text-xs text-gray-500">{selectedCustomer.gstNumber || 'Unregistered'}</p>
                  )}
                </div>

                <div className="rounded-md bg-indigo-50 px-3 py-2">
                  <p className="text-xs text-indigo-700">Tax on this invoice</p>
                  <p className="text-base font-semibold text-indigo-900">{GST_TYPE_LABELS[gstType]}</p>
                  <p className="mt-0.5 text-xs text-indigo-700">{taxReason}</p>
                </div>

                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Taxable amount</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.taxable)}</dd>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">after a discount of</dt>
                      <dd className="text-gray-500 tabular-nums">{formatMoney(totals.discount)}</dd>
                    </div>
                  )}
                  {gstType === 'cgst_sgst' && (
                    <>
                      <div className="flex justify-between gap-3">
                        <dt className="text-gray-600">CGST</dt>
                        <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.cgst)}</dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-gray-600">SGST</dt>
                        <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.sgst)}</dd>
                      </div>
                    </>
                  )}
                  {gstType === 'igst' && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">IGST</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.igst)}</dd>
                    </div>
                  )}
                  {totals.cess > 0 && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">Cess</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.cess)}</dd>
                    </div>
                  )}
                  {totals.roundOff !== 0 && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">Round off</dt>
                      <dd className="font-medium text-gray-900 tabular-nums">{formatMoney(totals.roundOff)}</dd>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-3">
                    <dt className="font-semibold text-gray-900">Grand total</dt>
                    <dd className="text-xl font-semibold text-gray-900 tabular-nums">{formatMoney(totals.grandTotal)}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <h4 className="text-sm font-semibold text-gray-900">What happens next</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>The invoice is saved as a draft and can still be changed.</li>
                <li>Issuing it fixes the amounts; after that it can only be paid or cancelled.</li>
                <li>The grand total is rounded to the nearest rupee.</li>
              </ul>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the total and the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">
              {items.length} {items.length === 1 ? 'item' : 'items'} · Grand total{' '}
              <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(totals.grandTotal)}</span>
            </span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? (isEdit ? 'Updating...' : 'Saving...') : isEdit ? 'Update Draft' : 'Save Draft'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default InvoiceForm;
